import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { MobileHeader } from "./MobileHeader";
import { UserCircle, SignOut, Desktop, ShieldCheck, DownloadSimple, Translate, Package } from "@phosphor-icons/react";
import { useInstallPrompt } from "../hooks/useInstallPrompt";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";

export default function MobileProfile() {
  const { t, i18n } = useTranslation();
  const isZh = (i18n.resolvedLanguage || i18n.language || "en").startsWith("zh");
  const { user, logout } = useAuth();
  const canSamples = ["admin", "sample_warehouse"].includes(user?.role);
  const navigate = useNavigate();
  const { canInstall, promptInstall, isStandalone, isIOS } = useInstallPrompt();
  const [showHelp, setShowHelp] = useState(false);

  const handleInstall = async () => {
    if (canInstall) {
      await promptInstall();
    } else {
      setShowHelp(true);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/m/login", { replace: true });
  };

  return (
    <div data-testid="mobile-profile-screen">
      <MobileHeader title={t("mobile.profile")} />

      <div className="px-4 pt-4">
        <div className="bg-white rounded-[16px] border border-[#E7E5E4] p-4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#1E3A8A]/8 flex items-center justify-center flex-shrink-0">
            <UserCircle size={28} weight="duotone" className="text-[#1E3A8A]" />
          </div>
          <div className="min-w-0">
            <p className="text-base font-medium text-[#1C1917] truncate" data-testid="mobile-profile-name">{user?.name}</p>
            <p className="text-sm text-[#78716C] truncate">{user?.email}</p>
            <span className="inline-flex items-center gap-1 mt-1.5 text-xs font-semibold px-2 py-0.5 rounded-full bg-[#9A3412]/10 text-[#9A3412]">
              <ShieldCheck size={12} weight="bold" /> {user?.role}
            </span>
          </div>
        </div>

        <div className="mt-4 bg-white rounded-[16px] border border-[#E7E5E4] overflow-hidden divide-y divide-[#F1F0EE]">
          <button
            onClick={() => i18n.changeLanguage(isZh ? "en" : "zh")}
            data-testid="mobile-profile-language-toggle"
            className="press-scale w-full p-4 flex items-center gap-3"
          >
            <Translate size={20} className="text-[#78716C]" />
            <span className="text-base text-[#1C1917] flex-1 text-left">{t("mobile.language")}</span>
            <span className="text-sm text-[#78716C]">{isZh ? "中文" : "English"}</span>
          </button>
          {!isStandalone && (
            <button
              onClick={handleInstall}
              data-testid="mobile-profile-install-app-button"
              className="press-scale w-full p-4 flex items-center gap-3"
            >
              <DownloadSimple size={20} className="text-[#78716C]" />
              <span className="text-base text-[#1C1917]">{t("mobile.install_app")}</span>
            </button>
          )}

          {canSamples && (
            <button onClick={() => navigate("/m/samples")} data-testid="mobile-profile-samples-link" className="press-scale w-full p-4 flex items-center gap-3">
              <Package size={20} className="text-[#78716C]" />
              <span className="text-base text-[#1C1917]">{t("samples.receive_title")}</span>
            </button>
          )}
          <button
            onClick={() => { window.location.href = "/"; }}
            data-testid="mobile-profile-desktop-link"
            className="press-scale w-full p-4 flex items-center gap-3"
          >
            <Desktop size={20} className="text-[#78716C]" />
            <span className="text-base text-[#1C1917]">{t("mobile.open_desktop")}</span>
          </button>
        </div>

        <div className="mt-4">

          <button
            onClick={handleLogout}
            data-testid="mobile-profile-logout-button"
            className="press-scale w-full bg-white border border-[#E7E5E4] rounded-[16px] p-4 flex items-center gap-3"
          >
            <SignOut size={20} className="text-[#9A3412]" />
            <span className="text-base font-medium text-[#9A3412]">{t("mobile.log_out")}</span>
          </button>
        </div>

      </div>

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent data-testid="mobile-install-help-dialog">
          <DialogHeader>
            <DialogTitle>{t("mobile.install_app")}</DialogTitle>
            <DialogDescription className="pt-2 text-left space-y-2">
              {isIOS ? (
                <>
                  <span className="block">Tap the Share icon in Safari's toolbar, then choose "Add to Home Screen".</span>
                </>
              ) : (
                <>
                  <span className="block">Open your browser menu and choose "Add to Home Screen" or "Install App".</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
}
